import { useState } from 'react'
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, InputAdornment, MenuItem, Select,
  Stack, Switch, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined'
import SearchIcon from '@mui/icons-material/Search'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import { motion } from 'framer-motion'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { Workflow } from '@cmms/api-client'

import { PageShell } from '../../layouts/PageShell'
import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { usePermissions } from '../../hooks/usePermissions'
import { useConfirm } from '../../components/ConfirmDialog'
import { CustomerScopePicker } from '../../components/CustomerScopePicker'
import { CustomerScopeChip } from '../../components/CustomerScopeChip'
import { TRIGGER_MAP, TRIGGER_TYPES, TRIGGER_CONDITIONS, TRIGGER_HELP, TRIGGER_EXAMPLES, STEP_TYPES, STEP_MAP } from './constants'
import { WorkflowBuilder } from './WorkflowBuilder'
import { ScheduleConfig } from './ScheduleConfig'
import { SearchableSelect } from '../../components/SearchableSelect'
import { formatSiteName } from '../../lib/siteDisplayName'

type TabFilter = 'all' | 'active' | 'inactive' | 'draft'

export function WorkflowsPage() {
  const qc = useQueryClient()
  const confirmDlg = useConfirm()
  const { can } = usePermissions()
  const canCreate = can('automations:create')
  const canUpdate = can('automations:update')
  const canDelete = can('automations:delete')
  const [createOpen, setCreateOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [tab, setTab] = useState<TabFilter>('all')
  const [search, setSearch] = useState('')

  const wfQ = useQuery({ queryKey: ['workflows'], queryFn: () => api.workflows.list() })
  const allWorkflows: Workflow[] = Array.isArray(wfQ.data) ? wfQ.data : []

  const filtered = allWorkflows.filter((wf) => {
    if (tab === 'active' && !wf.is_active) return false
    if (tab === 'inactive' && wf.is_active) return false
    if (tab === 'draft' && (wf.step_count ?? 0) > 0) return false
    if (search && !wf.name.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  const counts = {
    all: allWorkflows.length,
    active: allWorkflows.filter(w => w.is_active).length,
    inactive: allWorkflows.filter(w => !w.is_active).length,
    draft: allWorkflows.filter(w => (w.step_count ?? 0) === 0).length,
  }

  const toggleM = useMutation({
    mutationFn: (id: string) => api.workflows.toggle(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workflows'] }),
  })

  const deleteM = useMutation({
    mutationFn: (id: string) => api.workflows.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['workflows'] }); toast.success('Deleted') },
  })

  if (editingId) {
    return <WorkflowBuilder workflowId={editingId} onBack={() => { setEditingId(null); qc.invalidateQueries({ queryKey: ['workflows'] }) }} />
  }

  return (
    <PageShell title="Workflows" subtitle="Automate what happens before, during, and after work orders."
      actions={canCreate ? (
        <Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => setCreateOpen(true)} sx={{ fontSize: 13, fontWeight: 600, borderRadius: 999 }}>
          New workflow
        </Button>
      ) : undefined}>

      {/* Tabs + search */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, px: 1.5, fontSize: 12.5, fontWeight: 600, textTransform: 'none' } }}>
          <Tab value="all" label={`All ${counts.all}`} />
          <Tab value="active" label={`Active ${counts.active}`} />
          <Tab value="inactive" label={`Paused ${counts.inactive}`} />
          <Tab value="draft" label={`Drafts ${counts.draft}`} />
        </Tabs>
        <TextField placeholder="Search workflows..." value={search} onChange={(e) => setSearch(e.target.value)} size="small"
          sx={{ width: { xs: '100%', sm: 240 }, '& .MuiOutlinedInput-root': { fontSize: 13, bgcolor: 'background.paper', '& fieldset': { borderColor: 'divider' } } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: 'text.secondary' }} /></InputAdornment> }} />
      </Stack>

      {wfQ.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <PlayArrowOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', opacity: 0.3, mb: 2 }} />
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
            {search ? 'No workflows match your search' : 'No workflows yet'}
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.disabled', mb: 3 }}>
            {search ? 'Try a different search term.' : 'Create your first workflow to automate tasks.'}
          </Typography>
          {!search && <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} sx={{ borderRadius: 99, textTransform: 'none' }}>Create workflow</Button>}
        </Box>
      ) : (
        /* ── Table ── */
        <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          {/* Header */}
          <Stack direction="row" sx={{ px: 2.5, py: 1.25, bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
            <Typography sx={{ flex: 3, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Name</Typography>
            <Typography sx={{ flex: 2, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Trigger</Typography>
            <Typography sx={{ flex: 1, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', textAlign: 'center' }}>Steps</Typography>
            <Typography sx={{ flex: 1, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', textAlign: 'center' }}>Runs</Typography>
            <Typography sx={{ flex: 1.5, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Status</Typography>
            <Box sx={{ width: 80 }} />
          </Stack>

          {/* Rows */}
          {filtered.map((wf) => {
            const trigger = TRIGGER_MAP[wf.trigger_type]
            const triggerColor = trigger?.color ?? '#64748B'
            const TriggerIcon = trigger?.icon ?? PlayArrowOutlinedIcon
            const isDraft = (wf.step_count ?? 0) === 0

            return (
              <Stack key={wf.id} direction="row" alignItems="center" onClick={() => setEditingId(wf.id)}
                sx={{ px: 2.5, py: 1.75, borderTop: '1px solid', borderColor: 'divider', cursor: 'pointer',
                  transition: 'background 80ms', '&:hover': { bgcolor: 'action.hover' } }}>

                {/* Name */}
                <Box sx={{ flex: 3, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3 }} noWrap>{wf.name}</Typography>
                    <CustomerScopeChip customerId={wf.customer_id} />
                  </Stack>
                  {wf.description && (
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.15 }} noWrap>{wf.description}</Typography>
                  )}
                </Box>

                {/* Trigger */}
                <Box sx={{ flex: 2 }}>
                  <Chip icon={<TriggerIcon sx={{ fontSize: 13 }} />} label={trigger?.label ?? wf.trigger_type} size="small"
                    sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: `${triggerColor}12`, color: triggerColor,
                      '& .MuiChip-icon': { color: 'inherit' } }} />
                </Box>

                {/* Steps */}
                <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 500, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                  {wf.step_count ?? 0}
                </Typography>

                {/* Runs */}
                <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 500, textAlign: 'center', fontVariantNumeric: 'tabular-nums', color: (wf.run_count ?? 0) > 0 ? 'text.primary' : 'text.disabled' }}>
                  {wf.run_count ?? 0}
                </Typography>

                {/* Status */}
                <Box sx={{ flex: 1.5 }}>
                  {isDraft ? (
                    <Chip label="Draft" size="small" sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: '#F1F5F9', color: '#64748B' }} />
                  ) : wf.is_active ? (
                    <Chip icon={<FiberManualRecordIcon sx={{ fontSize: '8px !important' }} />} label="Active" size="small"
                      sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: '#ECFDF5', color: '#059669', '& .MuiChip-icon': { color: '#059669' } }} />
                  ) : (
                    <Chip label="Paused" size="small" sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: '#FFFBEB', color: '#D97706' }} />
                  )}
                </Box>

                {/* Actions */}
                <Stack direction="row" spacing={0.5} sx={{ width: 80, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                  <Tooltip title={wf.is_active ? 'Pause' : 'Activate'} arrow>
                    <Switch size="small" checked={wf.is_active} onChange={() => toggleM.mutate(wf.id)}
                      sx={{ '& .MuiSwitch-thumb': { width: 12, height: 12 }, '& .MuiSwitch-switchBase': { p: '6px' } }} />
                  </Tooltip>
                  <IconButton size="small" onClick={() => {
                    confirmDlg({ title: `Delete "${wf.name}"?`, message: 'This cannot be undone.', confirmLabel: 'Delete', variant: 'danger' })
                      .then((ok) => ok && deleteM.mutate(wf.id))
                  }} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Stack>
              </Stack>
            )
          })}
        </Box>
      )}

      {createOpen && <CreateWorkflowDialog onClose={() => setCreateOpen(false)} onCreated={(id) => { setCreateOpen(false); setEditingId(id) }} />}
    </PageShell>
  )
}

// ─── Create dialog ─────────────────────────────────────────────────────────

function CreateWorkflowDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState('')
  const [desc, setDesc] = useState('')
  const [trigger, setTrigger] = useState('')
  const [triggerConfig, setTriggerConfig] = useState<Record<string, any>>({})
  // Catalog scope. null = workflow fires for any customer in the org.
  const [customerId, setCustomerId] = useState<string | null>(null)

  const createM = useMutation({
    mutationFn: () => api.workflows.create({
      name: name.trim(), description: desc.trim() || undefined,
      trigger_type: trigger, trigger_config: triggerConfig,
      customer_id: customerId,
    }),
    onSuccess: (wf) => { toast.success('Workflow created'); onCreated(wf.id) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const conditions = TRIGGER_CONDITIONS[trigger] ?? []
  const example = TRIGGER_EXAMPLES[trigger]
  const selectedDef = TRIGGER_TYPES.find(t => t.key === trigger)

  // Site / location queries for the new picker-type conditions.
  // Locations only load once the site condition is set — matches the
  // scoped API (list-locations-by-site).
  const needsSitePicker = conditions.some((c) => c.type === 'site' || c.type === 'location')
  const sitesQ = useQuery({
    queryKey: ['sites'], queryFn: () => api.sites.list(),
    staleTime: 5 * 60_000, enabled: needsSitePicker,
  })
  const sites: any[] = Array.isArray(sitesQ.data) ? sitesQ.data : []
  const filterSite = triggerConfig.site_id
  const locationsQ = useQuery({
    queryKey: ['locations', filterSite],
    queryFn: () => api.locations.list(filterSite),
    enabled: Boolean(filterSite && conditions.some((c) => c.type === 'location')),
    staleTime: 60_000,
  })
  const locations: any[] = Array.isArray(locationsQ.data) ? locationsQ.data : []

  const setCondKey = (k: string, v: any) => {
    const n = { ...triggerConfig }
    if (v === '' || v === null || v === undefined) delete n[k]
    else n[k] = v
    if (k === 'site_id' && !v) delete n.location_id
    setTriggerConfig(n)
  }
  const timeWindow = (triggerConfig.time_window && typeof triggerConfig.time_window === 'object')
    ? triggerConfig.time_window : { start: '', end: '', tz: '' }

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, pb: 0.5 }}>New workflow</DialogTitle>
      <Typography sx={{ px: 3, fontSize: 13, color: 'text.secondary', mb: 1 }}>
        Give it a name, choose a trigger, and see how it works.
      </Typography>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField label="Workflow name" required value={name} onChange={(e) => setName(e.target.value)}
            size="small" fullWidth autoFocus placeholder="e.g. Critical WO approval chain" />
          <TextField label="Description (optional)" value={desc} onChange={(e) => setDesc(e.target.value)}
            size="small" fullWidth multiline minRows={2} placeholder="What does this workflow do?" />

          <CustomerScopePicker
            mode="catalog"
            value={customerId}
            onChange={setCustomerId}
            helperText="Leave as All so this workflow fires for every customer. Pick one to limit it to a single customer's work orders."
          />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            {/* Left — Trigger list */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1.5 }}>
                When should this run?
              </Typography>
              <Box sx={{ maxHeight: 340, overflowY: 'auto', scrollbarWidth: 'thin', pr: 0.5 }}>
                {TRIGGER_TYPES.map((t) => (
                  <Box key={t.key} onClick={() => { setTrigger(t.key); if (t.key !== trigger) setTriggerConfig({}) }}
                    sx={{ px: 1.75, py: 1, borderRadius: 1.5, cursor: 'pointer', mb: 0.5,
                      border: '1px solid', borderColor: trigger === t.key ? t.color : 'divider',
                      bgcolor: trigger === t.key ? `${t.color}08` : 'transparent',
                      transition: 'all 100ms', '&:hover': { borderColor: t.color } }}>
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Avatar sx={{ width: 26, height: 26, bgcolor: `${t.color}14`, color: t.color }}>
                        <t.icon sx={{ fontSize: 14 }} />
                      </Avatar>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{t.label}</Typography>
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Right — Preview */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {trigger && example ? (
                <Box sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
                  bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)', height: '100%' }}>
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
                    {selectedDef && <Avatar sx={{ width: 28, height: 28, bgcolor: `${selectedDef.color}14`, color: selectedDef.color }}><selectedDef.icon sx={{ fontSize: 15 }} /></Avatar>}
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{selectedDef?.label}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{example.useCase}</Typography>
                    </Box>
                  </Stack>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.6, mb: 2 }}>{example.description}</Typography>

                  {/* Mini example flow */}
                  <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 1 }}>Example</Typography>
                  <Stack alignItems="center" spacing={0}>
                    <MiniNode label="Trigger" sublabel={selectedDef?.label} color={selectedDef?.color ?? '#64748B'} />
                    {example.exampleFlow.map((s, i) => {
                      const sd = STEP_MAP[s.type]
                      return (<Box key={i} sx={{ width: '100%' }}><MiniLine /><MiniNode label={s.label} color={sd?.color ?? '#64748B'} icon={sd?.icon} num={i + 1} /></Box>)
                    })}
                  </Stack>

                  {/* Conditions */}
                  {trigger === 'schedule' ? (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 0.5 }}>Schedule</Typography>
                      <ScheduleConfig config={triggerConfig} onChange={setTriggerConfig} />
                    </Box>
                  ) : conditions.length > 0 && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 1 }}>Conditions</Typography>
                      <Stack spacing={1.5}>
                        {conditions.map((c) => (
                          <Box key={c.key}>
                            <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>{c.label}</Typography>
                            {c.type === 'select' ? (
                              <Select value={triggerConfig[c.key] ?? ''} onChange={(e) => setCondKey(c.key, e.target.value)}
                                size="small" fullWidth displayEmpty sx={{ fontSize: 12.5 }}>
                                {(c.options ?? []).map((o) => <MenuItem key={o.value} value={o.value} sx={{ fontSize: 12.5 }}>{o.label}</MenuItem>)}
                              </Select>
                            ) : c.type === 'checklist' ? (
                              <ChecklistConditionPicker value={triggerConfig[c.key] ?? ''} onChange={(v) => setCondKey(c.key, v)} />
                            ) : c.type === 'site' ? (
                              <SearchableSelect
                                value={triggerConfig[c.key] ?? ''}
                                onChange={(id) => setCondKey(c.key, id)}
                                options={sites}
                                getValue={(s: any) => s.id}
                                getLabel={formatSiteName}
                                placeholder="Any site"
                              />
                            ) : c.type === 'location' ? (
                              <SearchableSelect
                                value={triggerConfig[c.key] ?? ''}
                                onChange={(id) => setCondKey(c.key, id)}
                                options={locations}
                                getValue={(l: any) => l.id}
                                getLabel={(l: any) => l.name}
                                placeholder={filterSite ? 'Any location' : 'Pick a site first'}
                              />
                            ) : c.type === 'time_window' ? (
                              <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                  type="time" size="small" label="From"
                                  value={timeWindow.start ?? ''}
                                  onChange={(e) => setCondKey(c.key, { ...timeWindow, start: e.target.value })}
                                  InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
                                />
                                <TextField
                                  type="time" size="small" label="To"
                                  value={timeWindow.end ?? ''}
                                  onChange={(e) => setCondKey(c.key, { ...timeWindow, end: e.target.value })}
                                  InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
                                />
                                <TextField
                                  size="small" label="Timezone"
                                  value={timeWindow.tz ?? ''}
                                  onChange={(e) => setCondKey(c.key, { ...timeWindow, tz: e.target.value })}
                                  placeholder="Asia/Kolkata" sx={{ flex: 1.2 }}
                                />
                              </Stack>
                            ) : (
                              <TextField value={triggerConfig[c.key] ?? ''} onChange={(e) => setCondKey(c.key, e.target.value)}
                                size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { fontSize: 12.5 } }} />
                            )}
                            {c.helper && (
                              <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 0.5 }}>{c.helper}</Typography>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ p: 3, borderRadius: 2.5, border: '1px dashed', borderColor: 'divider', height: '100%',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <PlayArrowOutlinedIcon sx={{ fontSize: 36, color: 'text.disabled', opacity: 0.3, mb: 1 }} />
                  <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>Select a trigger to preview</Typography>
                </Box>
              )}
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || !trigger || createM.isPending} onClick={() => createM.mutate()}
          sx={{ borderRadius: 2, fontWeight: 600 }}>{createM.isPending ? 'Creating...' : 'Create & configure'}</Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Mini flow nodes ────────────────────────────────────────────────────────

function MiniNode({ label, sublabel, color, icon: Icon, num }: { label: string; sublabel?: string; color: string; icon?: any; num?: number }) {
  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1}
        sx={{ px: 1.5, py: 0.75, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: `${color}14`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {Icon ? <Icon sx={{ fontSize: 11 }} /> : <Typography sx={{ fontSize: 8, fontWeight: 800 }}>{num ?? '▶'}</Typography>}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1.2 }} noWrap>{label}</Typography>
          {sublabel && <Typography sx={{ fontSize: 9.5, color: 'text.secondary' }} noWrap>{sublabel}</Typography>}
        </Box>
      </Stack>
    </Box>
  )
}

function MiniLine() { return <Box sx={{ width: 1.5, height: 10, bgcolor: 'divider', mx: 'auto' }} /> }

function ChecklistConditionPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const clQ = useQuery({ queryKey: ['checklists', { status: 'published' }], queryFn: () => api.checklists.list({ status: 'published' }), staleTime: 60_000 })
  const checklists: any[] = Array.isArray(clQ.data) ? clQ.data : []

  return (
    <SearchableSelect
      value={value}
      onChange={onChange}
      options={checklists}
      getValue={(c) => c.id}
      getLabel={(c) => c.name}
      placeholder="Any checklist"
      renderOption={(c) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <ChecklistOutlinedIcon sx={{ fontSize: 14, color: '#059669' }} />
          <span>{c.name}</span>
        </Stack>
      )}
    />
  )
}
